import React, { useState, useEffect } from "react";
import {
  Chart,
  Settings,
  LineSeries,
  Axis,
  Position,
  Tooltip,
  DARK_THEME,
  LIGHT_THEME,
  ScaleType,
  timeFormatter,
} from "@elastic/charts";
import Loader from "../../../shared/Loader";
import { useTheme } from "../../../../context/ThemeContext";
import StatsService from "../../../../services/v2/inbound/stats-service";

const TimelineAverages = ({ filters, timeInterval }) => {
  const [data, setData] = useState(null);
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const { isDarkMode } = useTheme();

  // Convertir mm:ss a segundos
  const timeToSeconds = (time) => {
    const [minutes, seconds] = time.split(":").map(Number);
    return minutes * 60 + seconds;
  };

  // Convertir segundos a mm:ss para etiquetas
  const secondsToTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  useEffect(() => {
    setLoading(true);
    setError(false);

    const payload = { ...filters, timeInterval };

    StatsService.timelineAverages(payload)
      .then(({ data: { data } }) => {
        // Transformar los datos para adaptarlos al gráfico
        const transformedData = data
          .map((item) => [
            {
              x: item.timeInterval,
              y: timeToSeconds(item.avgDurationTime),
              g: "Duration Time",
              label: item.avgDurationTime,
            },
            {
              x: item.timeInterval,
              label: item.avgRingingTime,
              y: timeToSeconds(item.avgRingingTime),
              g: "Ringing Time",
            },
          ])
          .flat();

        setData(transformedData);
        setLoading(false);
      })
      .catch((error) => {
        console.error(error);
        setError(true);
        setLoading(false);
      });
  }, [filters, timeInterval]);

  if (isLoading)
    return (
      <div className="flex items-center justify-center w-full h-full">
        <Loader />
      </div>
    );

  if (error)
    return (
      <div className="flex items-center justify-center w-full h-full">
        <div className="text-red-500 text-sm font-semibold text-center">
          Some error occurred loading data
        </div>
      </div>
    );

  const formatter = (time) => {
    return new Intl.DateTimeFormat("es-ES", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(time));
  };

  return (
    <div className={`w-full h-full ${isDarkMode ? "dark-legend" : ""}`}>
      <Chart size="100%">
        <Settings
          showLegend={true}
          legendPosition="left"
          baseTheme={isDarkMode ? DARK_THEME : LIGHT_THEME}
          theme={{
            background: {
              color: isDarkMode ? "#1A202C" : "#ffffff",
            },
            axes: {
              tickLine: {
                stroke: isDarkMode ? "#ffffff" : "#000000",
              },
              tickLabel: {
                fill: isDarkMode ? "#ffffff" : "#000000",
              },
            },
          }}
        />
        <Tooltip
          style={{
            backgroundColor: isDarkMode ? "#1f2937" : "#ffffff",
            color: isDarkMode ? "#ffffff" : "#000000",
            border: `1px solid ${isDarkMode ? "#4B5563" : "#E5E7EB"}`,
          }}
          headerFormatter={({ value }) => formatter(value)}
          valueFormatter={({ label }) => label}
        />
        <LineSeries
          id="timeline-averages"
          xScaleType={ScaleType.Time}
          yScaleType={ScaleType.Linear}
          xAccessor="x" // Eje X
          yAccessors={["y"]} // Valores del eje Y
          splitSeriesAccessors={["g"]} // Divide las series por tipo
          data={data}
        />
        <Axis
          id="bottom-axis"
          position={Position.Bottom}
          showOverlappingTicks
          tickFormat={formatter} // Formato dinámico de fechas
          tickLabelStyle={{
            fill: isDarkMode ? "#ffffff" : "#000000",
          }}
        />
        <Axis
          id="left-axis"
          position={Position.Left}
          tickFormat={(d) => secondsToTime(d)} // Formato en mm:ss
          tickLabelStyle={{
            fill: isDarkMode ? "#ffffff" : "#000000",
          }}
        />
      </Chart>
    </div>
  );
};

export default TimelineAverages;
